export const ALARM_SHARE_LIST_HIDDEN_MARKER = '__alarm_share_hidden__';

const safeParseRawDataList = (value) => {
  if (!value) return null;
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

export const shouldHideGlobalActionFromList = (item) => {
  if (!item || typeof item !== 'object') return false;

  if (
    item.triggerName === ALARM_SHARE_LIST_HIDDEN_MARKER ||
    item.resolutionName === ALARM_SHARE_LIST_HIDDEN_MARKER
  ) {
    return true;
  }

  const rawDataList =
    safeParseRawDataList(item.rawDataList) ?? safeParseRawDataList(item.raw_data_list);

  if (!rawDataList || rawDataList.length === 0) return false;

  const firstRaw = rawDataList[0];
  return Boolean(firstRaw?.hiddenFromGlobalActionsList === true);
};

/**
 * Extracts all related device IDs from a global action.
 * Looks into actions, triggers, and resolutions (per GlobalAction types).
 * @param {import("../../ActionBuilder/types/GlobalAction.types").GlobalAction} globalAction - The global action object (see GlobalAction.types)
 * @returns {Array<number|string>} Array of unique deviceId and nodeId values found.
 */
export function getRelatedDevicesFromGlobalAction(globalAction) {
  const deviceIds = new Set();

  function extractFromParams(params) {
    if (!params || typeof params !== 'object') return;
    if ('deviceId' in params && params.deviceId != null) {
      deviceIds.add(params.deviceId);
    }
  }

  // Actions
  if (Array.isArray(globalAction.actions)) {
    globalAction.actions.forEach((action) => {
      extractFromParams(action.params);
    });
  }

  // Triggers (array of arrays)
  if (Array.isArray(globalAction.triggers)) {
    globalAction.triggers.flat().forEach((trigger) => {
      extractFromParams(trigger);
    });
  }

  // Resolutions (array of arrays)
  if (Array.isArray(globalAction.resolutions)) {
    globalAction.resolutions.flat().forEach((resolution) => {
      extractFromParams(resolution);
    });
  }

  return Array.from(deviceIds);
}

/**
 * Checks if any device IDs referenced in the global action are missing from the provided device list.
 * @param {import("../../ActionBuilder/types/GlobalAction.types").GlobalAction} globalAction - The global action object (see GlobalAction.types)
 * @param {Array<{ id: number }>} deviceList - The list of available devices (should contain id property)
 * @returns {boolean} True if any referenced deviceId is missing from deviceList, false otherwise
 */
export function isMissingDevice(globalAction, deviceList) {
  const deviceIds = getRelatedDevicesFromGlobalAction(globalAction);
  if (!Array.isArray(deviceList)) return false;
  const availableIds = new Set(deviceList.map((device) => device.id));
  const missing = deviceIds.filter((id) => !availableIds.has(id));
  return missing.length > 0;
}

/**
 * Returns all global actions related to a given device id.
 * @param {number|string} deviceId - The device id to search for.
 * @param {Array<import("../../ActionBuilder/types/GlobalAction.types").GlobalAction>} globalActionsList - The list of global actions.
 * @returns {Array<string>} Array of global action titles related to the device.
 */
export function getRelatedGlobalActionsFromDevice(deviceId, globalActionsList) {
  if (!globalActionsList || !Array.isArray(globalActionsList)) return [];
  return globalActionsList
    .filter((action) => {
      const relatedIds = getRelatedDevicesFromGlobalAction(action);
      return relatedIds.includes(deviceId);
    })
    .map((action) => action.title);
}

/**
 * Returns all global actions related to a given group.
 * @param {import("features/Integrations/features/ConnectedDeviceGroups/groups.types").Group} group - The group object containing devices IDs.
 * @param {Array<import("../../ActionBuilder/types/GlobalAction.types").GlobalAction>} globalActionsList - The list of global actions.
 * @returns {Array<string>} Array of global action titles related to the group.
 */
export function getRelatedGlobalActionsFromGroup(group, globalActionsList) {
  if (!globalActionsList || !Array.isArray(globalActionsList)) return [];
  // Combine all device IDs from devices, controllers, and aux_devices
  const allGroupDeviceIds = [
    ...(Array.isArray(group.devices) ? group.devices : []),
    ...(Array.isArray(group.controllers) ? group.controllers : []),
    ...(Array.isArray(group.aux_devices) ? group.aux_devices : []),
  ];
  return globalActionsList
    .filter((action) => {
      const relatedIds = getRelatedDevicesFromGlobalAction(action);
      return allGroupDeviceIds.some((deviceId) => relatedIds.includes(deviceId));
    })
    .map((action) => action.title);
}
